import React, { useState, useEffect } from 'react';
import { Tag, Spin, Empty, Progress, Badge } from 'antd';
import {
  Ticket, Bug, Zap, BarChart3, FileText, CheckCircle, Clock,
  PauseCircle, AlertTriangle, TrendingDown, ArrowUpRight,
} from 'lucide-react';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Filler,
  Tooltip as CTooltip, Legend as CLegend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import { TICKETING_DATA } from '../../constants/api';
import ExportButton from '../../components/common/ExportButton';
import { MetricCard, HeadlineCard } from '../../components/insights';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Filler, CTooltip, CLegend
);

const SupportOverview = () => {
  const { fetch } = useApiMethods();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bug Trend filters — independent for Production vs Development.
  // Values: 'all' (default, both stacks visible) | 'open' | 'completed'
  const [prodBugFilter, setProdBugFilter] = useState('all');
  const [devBugFilter, setDevBugFilter] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      const res = await fetch(TICKETING_DATA, {}, setLoading);
      if (res?.status !== 'fail') setData(res?.data || res);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><Spin size="large" /></div>;
  if (!data) return <Empty description="Unable to load data" className="mt-20" />;

  const { summary = {}, sprint_progress = [], backlog_trend = [], bug_trend = {}, client_requirements = {} } = data;
  const sprints = Array.isArray(sprint_progress) ? sprint_progress : sprint_progress ? [sprint_progress] : [];
  const cr = client_requirements;
  const cr_clients = cr.clients || [];
  const { open_tickets = 0, total_tickets = 0, by_type = {}, by_priority = [], by_status = [], production_bugs = 0 } = summary;
  const avgCompletion = sprints.length > 0 ? Math.round(sprints.reduce((s, sp) => s + (sp.completion_pct || 0), 0) / sprints.length) : 0;

  // Bug Trend now arrives as {trend, unassigned, summary}; tolerate legacy list shape.
  const bugTrendList = Array.isArray(bug_trend) ? bug_trend : (bug_trend.trend || []);
  const bugSummary = Array.isArray(bug_trend) ? null : (bug_trend.summary || null);
  const bugUnassigned = Array.isArray(bug_trend) ? null : (bug_trend.unassigned || null);

  // ── Helpers ──
  // Color resolvers for the status / priority breakdowns
  const STATUS_COLORS = {
    'Open': '#6366f1', 'New': '#6366f1', 'To Do': '#6366f1',
    'In Dev': '#3b82f6', 'In Progress': '#3b82f6', 'Development': '#3b82f6',
    'In QA': '#a855f7', 'QA': '#a855f7', 'Testing': '#a855f7',
    'On Hold': '#f59e0b', 'Hold': '#f59e0b', 'Blocked': '#ef4444',
    'Ready': '#10b981', 'Deferred': '#94a3b8', 'Backlog': '#64748b',
  };
  const PRIORITY_COLORS_MAP = { Critical: '#ef4444', High: '#f97316', Medium: '#eab308', Low: '#10b981' };
  const statusColor = (label) => STATUS_COLORS[label] || '#94a3b8';
  const priorityColor = (label) => PRIORITY_COLORS_MAP[label] || '#94a3b8';

  const BreakdownCard = ({ title, items, total, colorFor }) => (
    <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-foreground">{title}</h4>
        <span className="text-xs text-muted-foreground">Total {total}</span>
      </div>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const pct = total > 0 ? Math.round((it.count / total) * 100) : 0;
            return (
              <div key={it.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-foreground">{it.label}</span>
                  <span className="text-muted-foreground">{it.count} ({pct}%)</span>
                </div>
                <Progress percent={pct} showInfo={false} strokeColor={colorFor(it.label)} size="small" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Bridge legacy KpiCard API → new MetricCard
  const KpiCard = ({ icon, iconBg = '', title, value, subtitle, alert }) => {
    const IconComp = icon?.type;
    const intent = alert ? 'negative'
      : iconBg.includes('emerald') || iconBg.includes('green') ? 'positive'
      : iconBg.includes('red') || iconBg.includes('rose') ? 'negative'
      : iconBg.includes('amber') || iconBg.includes('orange') || iconBg.includes('yellow') ? 'warning'
      : iconBg.includes('blue') || iconBg.includes('sky') || iconBg.includes('cyan') ? 'info'
      : iconBg.includes('indigo') || iconBg.includes('violet') || iconBg.includes('purple') ? 'primary'
      : 'neutral';
    return (
      <MetricCard icon={IconComp} intent={intent} title={title} value={value} subtitle={subtitle} />
    );
  };

  // ── Chart configs ──
  const lineOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } }, y: { grid: { color: 'rgba(148,163,184,0.15)' } } },
  };
  // Chart.js plugin: render each stacked bar's grand total just above the top segment
  const stackTotalsPlugin = {
    id: 'stackTotals',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      if (!data?.labels?.length) return;
      ctx.save();
      ctx.fillStyle = 'rgba(148,163,184,0.95)';
      ctx.font = '600 11px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      data.labels.forEach((_, i) => {
        let total = 0;
        let topY = null;
        let xPos = null;
        data.datasets.forEach((ds, dsIdx) => {
          if (!chart.isDatasetVisible(dsIdx)) return;
          const val = Number(ds.data[i]) || 0;
          total += val;
          const bar = chart.getDatasetMeta(dsIdx)?.data?.[i];
          if (bar) {
            if (topY === null || bar.y < topY) topY = bar.y;
            xPos = bar.x;
          }
        });
        if (total > 0 && topY !== null && xPos !== null) {
          ctx.fillText(String(total), xPos, topY - 6);
        }
      });
      ctx.restore();
    },
  };

  const barOpts = (stacked = false) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: stacked ? { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } : { display: false } },
    scales: {
      x: { stacked, grid: { display: false } },
      y: { stacked, beginAtZero: true, grid: { color: 'rgba(148,163,184,0.15)' } },
    },
  });

  const typeEntries = Object.entries(by_type);

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">

      {/* ━━ Header ━━ */}
      <div>
        <h2 className="text-2xl font-bold text-foreground">Development</h2>
        <p className="text-sm text-muted-foreground">Ticket metrics and sprint progress from FixDesk</p>
      </div>

      {/* Headline */}
      <HeadlineCard
        state={production_bugs > 5 ? 'critical' : (open_tickets > 500 || avgCompletion < 30) ? 'warning' : 'healthy'}
        title={
          production_bugs > 5
            ? `${production_bugs} production bugs open — customer impact likely`
            : avgCompletion < 30 && sprints.length > 0
              ? `Sprint completion low at ${avgCompletion}% across ${sprints.length} sprints`
              : `${open_tickets} open tickets · ${sprints.length} active sprints at ${avgCompletion}% avg`
        }
        description={`${total_tickets} total tickets · ${(cr.total_requirements || 0)} client requirements tracked`}
        metrics={[
          { label: 'Open', value: open_tickets },
          { label: 'Bugs', value: production_bugs },
          { label: 'Sprints', value: sprints.length, hint: `${avgCompletion}% avg` },
          { label: 'CR Pending', value: cr.pending || 0 },
        ]}
      />

      {/* ━━ KPI Cards — Tickets ━━ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<BarChart3 className="w-5 h-5 text-white" />} iconBg="bg-blue-500"
          title="Total Active" value={total_tickets}
        />
        <KpiCard
          icon={<Ticket className="w-5 h-5 text-white" />} iconBg="bg-indigo-500"
          title="Open Tickets" value={open_tickets}
          subtitle={`${total_tickets} total across all statuses`}
        />
        <KpiCard
          icon={<Bug className="w-5 h-5 text-white" />}
          iconBg={production_bugs > 5 ? 'bg-red-500' : 'bg-gray-500'}
          title="Production Bugs" value={production_bugs}
          subtitle={production_bugs > 0 ? 'Customer impact risk' : 'All clear'}
          alert={production_bugs > 5}
        />
        <KpiCard
          icon={<Zap className="w-5 h-5 text-white" />} iconBg="bg-amber-500"
          title="Active Sprints" value={sprints.length}
          subtitle={sprints.length > 0 ? `${avgCompletion}% avg completion` : 'No active sprints'}
        />
      </div>

      {/* ━━ Active Sprints ━━ */}
      {sprints.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground">Sprint Progress</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sprints.map((sp) => {
              const pct = sp.completion_pct || 0;
              const color = pct >= 70 ? '#10b981' : pct >= 40 ? '#6366f1' : '#f59e0b';
              return (
                <div key={sp.sprint_name} className="rounded-2xl bg-card border border-border shadow-sm p-5 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-foreground">{sp.sprint_name}</div>
                      {sp.team_name && <span className="text-xs text-muted-foreground">{sp.team_name}</span>}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color }}>{pct}%</div>
                    </div>
                  </div>
                  <Progress percent={pct} showInfo={false} strokeColor={color} strokeWidth={6} className="mb-3" />
                  <div className="flex justify-between text-xs text-muted-foreground mb-3">
                    <span>{sp.start_date} — {sp.end_date}</span>
                    <span>{sp.total} tickets</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">{sp.done} Done</span>
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-950/30 text-blue-600">{sp.in_progress} Active</span>
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">{sp.pending} Pending</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ━━ Bug Trend — Production + Development (sprint-bucketed) ━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(() => {
          // Canonical totals come from bugSummary so they match the "Production Bugs"
          // KPI / Need-Attention banner. Falls back to summing the trend buckets when
          // the backend hasn't been updated yet.
          const prodOpen = bugSummary?.production_open ?? bugTrendList.reduce((s, b) => s + (b.production_open ?? 0), 0) + (bugUnassigned?.production_open ?? 0);
          const prodDone = bugSummary?.production_completed ?? bugTrendList.reduce((s, b) => s + (b.production_completed ?? 0), 0) + (bugUnassigned?.production_completed ?? 0);
          const prodTotal = bugSummary?.production_total ?? (prodOpen + prodDone);
          const devOpen = bugSummary?.development_open ?? bugTrendList.reduce((s, b) => s + (b.development_open ?? 0), 0) + (bugUnassigned?.development_open ?? 0);
          const devDone = bugSummary?.development_completed ?? bugTrendList.reduce((s, b) => s + (b.development_completed ?? 0), 0) + (bugUnassigned?.development_completed ?? 0);
          const devTotal = bugSummary?.development_total ?? (devOpen + devDone);
          const labels = bugTrendList.map(b => b.sprint_name || b.week);
          const noProd = bugTrendList.every(b => !(b.production_open || b.production_completed || b.production));
          const noDev = bugTrendList.every(b => !(b.development_open || b.development_completed || b.development));

          // Small reusable segmented filter — "All | Open | Completed"
          const FilterPills = ({ value, onChange }) => (
            <div className="inline-flex items-center rounded-md border border-border bg-background p-0.5 text-[11px]">
              {[
                { v: 'all',       label: 'All' },
                { v: 'open',      label: 'Open' },
                { v: 'completed', label: 'Completed' },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => onChange(opt.v)}
                  className={
                    'px-2 py-0.5 rounded-[5px] transition-colors ' +
                    (value === opt.v
                      ? 'bg-foreground text-background font-medium'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          );

          // Filter datasets for a card based on its active filter
          const buildDatasets = (filter, completedData, openData, openColor, stackId) => {
            const completed = { label: 'Completed', data: completedData, backgroundColor: '#10b981', borderRadius: 6, barThickness: 24, stack: stackId };
            const open      = { label: 'Open',      data: openData,      backgroundColor: openColor, borderRadius: 6, barThickness: 24, stack: stackId };
            if (filter === 'open')      return [open];
            if (filter === 'completed') return [completed];
            return [completed, open]; // 'all'
          };

          return (
            <>
              <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h4 className="font-semibold text-foreground">Production Bug Trend</h4>
                  <FilterPills value={prodBugFilter} onChange={setProdBugFilter} />
                </div>
                <div className="flex items-center gap-4 mb-3 text-xs">
                  <span className="text-muted-foreground">Total <span className="font-semibold text-foreground">{prodTotal}</span></span>
                  {(prodBugFilter === 'all' || prodBugFilter === 'completed') && (
                    <span className="text-emerald-600 dark:text-emerald-400">Completed <span className="font-semibold">{prodDone}</span></span>
                  )}
                  {(prodBugFilter === 'all' || prodBugFilter === 'open') && (
                    <span className="text-red-500">Open <span className="font-semibold">{prodOpen}</span></span>
                  )}
                </div>
                {bugTrendList.length === 0 || noProd ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No production bugs in any sprint" />
                ) : (
                  <div style={{ height: 260 }}>
                    <Bar
                      data={{
                        labels,
                        datasets: buildDatasets(
                          prodBugFilter,
                          bugTrendList.map(b => b.production_completed ?? 0),
                          bugTrendList.map(b => b.production_open ?? b.production ?? 0),
                          '#ef4444',
                          'prod',
                        ),
                      }}
                      options={{ ...barOpts(true), layout: { padding: { top: 22 } } }}
                      plugins={[stackTotalsPlugin]}
                    />
                  </div>
                )}
                {bugUnassigned && (bugUnassigned.production_open + bugUnassigned.production_completed) > 0 && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    + {bugUnassigned.production_open} open / {bugUnassigned.production_completed} done not assigned to a sprint
                  </div>
                )}
              </div>
              <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h4 className="font-semibold text-foreground">Development Bug Trend</h4>
                  <FilterPills value={devBugFilter} onChange={setDevBugFilter} />
                </div>
                <div className="flex items-center gap-4 mb-3 text-xs">
                  <span className="text-muted-foreground">Total <span className="font-semibold text-foreground">{devTotal}</span></span>
                  {(devBugFilter === 'all' || devBugFilter === 'completed') && (
                    <span className="text-emerald-600 dark:text-emerald-400">Completed <span className="font-semibold">{devDone}</span></span>
                  )}
                  {(devBugFilter === 'all' || devBugFilter === 'open') && (
                    <span className="text-amber-500">Open <span className="font-semibold">{devOpen}</span></span>
                  )}
                </div>
                {bugTrendList.length === 0 || noDev ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No development bugs in any sprint" />
                ) : (
                  <div style={{ height: 260 }}>
                    <Bar
                      data={{
                        labels,
                        datasets: buildDatasets(
                          devBugFilter,
                          bugTrendList.map(b => b.development_completed ?? 0),
                          bugTrendList.map(b => b.development_open ?? b.development ?? 0),
                          '#f59e0b',
                          'dev',
                        ),
                      }}
                      options={{ ...barOpts(true), layout: { padding: { top: 22 } } }}
                      plugins={[stackTotalsPlugin]}
                    />
                  </div>
                )}
                {bugUnassigned && (bugUnassigned.development_open + bugUnassigned.development_completed) > 0 && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    + {bugUnassigned.development_open} open / {bugUnassigned.development_completed} done not assigned to a sprint
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* ━━ Open Tickets Breakdown: By Status + By Priority ━━ */}
      {(by_status.length > 0 || by_priority.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BreakdownCard
            title="Open Tickets by Status"
            items={by_status.map((s) => ({ label: s.status, count: s.count }))}
            total={open_tickets}
            colorFor={statusColor}
          />
          <BreakdownCard
            title="Open Tickets by Priority"
            items={by_priority.map((p) => ({ label: p.priority, count: p.count }))}
            total={open_tickets}
            colorFor={priorityColor}
          />
        </div>
      )}

      {/* ━━ KPI Cards — Client Requirements ━━ */}
      {(cr.total_requirements || 0) > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground">Client Requirements</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={<FileText className="w-5 h-5 text-white" />} iconBg="bg-violet-500"
              title="Total Requirements" value={cr.total_requirements || 0}
            />
            <KpiCard
              icon={<CheckCircle className="w-5 h-5 text-white" />} iconBg="bg-emerald-500"
              title="Completed" value={cr.completed || 0}
              subtitle={`${((cr.total_requirements || 0) - (cr.deferred || 0)) > 0 ? Math.round((cr.completed || 0) / ((cr.total_requirements || 0) - (cr.deferred || 0)) * 100) : 0}% completion rate`}
            />
            <KpiCard
              icon={<Clock className="w-5 h-5 text-white" />} iconBg="bg-orange-500"
              title="Pending" value={cr.pending || 0}
              alert={(cr.pending || 0) > 100}
            />
            <KpiCard
              icon={<PauseCircle className="w-5 h-5 text-white" />} iconBg="bg-gray-400"
              title="Deferred" value={cr.deferred || 0}
            />
          </div>

          {(cr.pending_by_status?.length || 0) > 0 && (
            <div className="rounded-2xl bg-card border border-border shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-orange-500" />
                <h4 className="text-sm font-semibold text-foreground">Pending by status</h4>
                <span className="text-xs text-muted-foreground ml-auto">{cr.pending || 0} pending total</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {cr.pending_by_status.map((s) => (
                  <span
                    key={s.status_name}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30"
                  >
                    <span className="font-bold tabular-nums">{s.count}</span>
                    <span className="text-amber-600 dark:text-amber-400">{s.status_name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ━━ Client Requirements Table ━━ */}
      {cr_clients.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-violet-500" />
            <h4 className="font-semibold text-foreground">Client Requirements Breakdown</h4>
            <ExportButton
              data={cr_clients}
              filename="client-requirements"
              pdfColumns={[{title:'Client',dataKey:'client_name'},{title:'Project',dataKey:'project'},{title:'Total',dataKey:'total'},{title:'Completed',dataKey:'completed'},{title:'Pending',dataKey:'pending'},{title:'Deferred',dataKey:'deferred'}]}
              pdfTitle="Client Requirements Report"
            />
          </div>
          <div className="space-y-3">
            {cr_clients.map((client) => {
              const activeTotal = (client.total || 0) - (client.deferred || 0);
              const completionPct = activeTotal > 0 ? Math.round((client.completed || 0) / activeTotal * 100) : 0;
              return (
                <div key={client.client_code || client.client_name} className="flex items-center gap-4 p-3 rounded-xl bg-background hover:bg-gray-100/50 transition-colors">
                  <div className="w-40 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{client.client_name}</div>
                    {client.project && <div className="text-[10px] text-muted-foreground">{client.project}</div>}
                  </div>
                  <div className="flex-1">
                    <Progress
                      percent={completionPct}
                      strokeColor={completionPct >= 70 ? '#10b981' : completionPct >= 40 ? '#6366f1' : '#f59e0b'}
                      size="small"
                      format={() => <span className="text-xs text-muted-foreground">{completionPct}%</span>}
                    />
                  </div>
                  {/* Fixed-width right column so the progress bar above is identical across rows */}
                  <div className="flex gap-2 text-xs shrink-0 w-64 justify-end">
                    <span className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">{client.completed || 0}</span>
                    <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">{client.pending || 0}</span>
                    {(client.deferred || 0) > 0 && (
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-muted-foreground">{client.deferred} deferred</span>
                    )}
                    <span className="text-muted-foreground tabular-nums w-16 text-right">{client.total} total</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ━━ Supporting charts — By Type + Backlog Trend ━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Type Doughnut */}
        <div className="lg:col-span-4 rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">By Type</h4>
          {typeEntries.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div style={{ height: 220 }}>
              <Doughnut
                data={{
                  labels: typeEntries.map(([k]) => k),
                  datasets: [{
                    data: typeEntries.map(([, v]) => v),
                    backgroundColor: typeEntries.map(([k]) => ({ Story: '#6366f1', Bug: '#ef4444', Epic: '#f59e0b' }[k] || '#94a3b8')),
                    borderWidth: 0,
                  }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } } }}
              />
            </div>
          )}
        </div>

        {/* Backlog Trend */}
        <div className="lg:col-span-8 rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Backlog Trend</h4>
          {backlog_trend.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div style={{ height: 320 }}>
              <Line
                data={{
                  labels: backlog_trend.map(b => b.week),
                  datasets: [{
                    label: 'Open', data: backlog_trend.map(b => b.count),
                    borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.08)',
                    borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#6366f1', tension: 0.4, fill: true,
                  }],
                }}
                options={lineOpts}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportOverview;
